import { Device, i18n } from '@goodboydigital/astro';
import gsap from 'gsap';
import { Container, Sprite } from 'pixi.js';

import { App, Shared } from '../App';
import { Settings } from '../AppConfig';
import { TrailEntity } from '../game/entities/TrailEntity';
import { AnalyticsEvent, AnalyticsState } from '../plugins/AnalyticsPlugin';
import { NineSliceButton } from '../ui/buttons/NineSliceButton';
import LocalisedText from '../ui/LocalisedText';
import { PurchasePanel } from '../ui/PurchasePanel';
import { SwipeScroller } from '../ui/SwipeScroller';
import { RadioSelector } from './../ui/RadioSelector';
import { ShopPanel } from './../ui/ShopPanel';
import { RideStudioBaseShopScreen, RideStudioBaseShopScreenOptions } from './RideStudioBaseShopScreen';
export class RideStudioTrailShopScreen extends RideStudioBaseShopScreen
{
    protected options: RideStudioBaseShopScreenOptions;
    protected selectedText!: LocalisedText;
    protected tickIcon!: Sprite;

    private _currentPartID: string;
    private _startPartID: string;
    private _selectContainer!: Sprite;
    private _paintSelector!: RadioSelector;

    private _leftSwipeScroller!: SwipeScroller;

    constructor(app: App, options: RideStudioBaseShopScreenOptions)
    {
        super(app, options);

        this.type = 'trail';

        this.options = options;
        this._currentPartID = '';
        this._startPartID = '';

        // resize and re-order icons on settings change for consistency across app
        Shared.settings.shopIconSizeChanged.connect((newSize) =>
        {
            if (!this._paintSelector) return;

            this._paintSelector.resizeButtons(newSize);
            gsap.killTweensOf(this._paintSelector.view);
            this._paintSelector.view.y = 0;
        });
    }

    public show(): void
    {
        super.show();
        
        this.app.analytics.trackState(AnalyticsState.TRAILS);
        
        this.selectedText.visible = !Settings.spineDebug;
        this._selectContainer.visible = !Settings.spineDebug;

        this.updateTrailIcon();

        this.rightPanel.titleText.setText('rideStudioTrailShopScreen_type');
        this._currentPartID = Shared.game.garage.selectedTrail;
        this._startPartID = this._currentPartID;
        this.displayItemUnlockedStatus();
        this._leftSwipeScroller.refreshContent();
    }

    public hide(): void
    {
        
        // Shared.game.shop.saveVehicleSelection();
    }

    protected buildRightPanel(): void
    {
        this.rightPanel = new ShopPanel({
            title: '',
            w: this.panelWidth,
            h: this.panelHeight,
            radioButtonOptions: [],
            omitArrows: Device.mobile,
        });
        this.view.addChild(this.rightPanel.view);

        this._leftSwipeScroller = this.createSwipeScroller();
        this.rightPanel.content.addChild(this._leftSwipeScroller.view);

        // connect buttons
        this.rightPanel.signals.onArrowUp.connect(() =>
        {
            this._leftSwipeScroller.nextItem();
            //this.displayItemUnlockedStatus();
            
            this.selectedText.visible = false;
            this.tickIcon.visible = false;
        });
        this.rightPanel.signals.onArrowDown.connect(() =>
        {
            this._leftSwipeScroller.prevItem();
            //this.displayItemUnlockedStatus();
            
            this.selectedText.visible = false;
            this.tickIcon.visible = false;
        });

        const selectIcon = new Sprite();
        const selectBgWidth = 180;
        const selectBgHeight = 60;

        selectIcon.anchor.set(0.5);

        this.selectedText = new LocalisedText('rideStudioTrailShopScreen_selectedText', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 6,
            lineJoin: 'round',
            align: 'center',
            fontSize: 26,
            maxWidth: 250,
        });

        this.selectedText.anchor.set(0.5);
        this.selectedText.y = 65;
        this.rightPanel.view.addChild(this.selectedText);

        const selectText = new LocalisedText('rideStudioTrailShopScreen_selectText', {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            stroke: 0x333333,
            strokeThickness: 6,
            lineJoin: 'round',
            align: 'center',
            fontSize: 26,
            maxWidth: 165,
        });

        selectText.anchor.set(0.5);
        selectIcon.addChild(selectText);
        
        this.tickIcon = Sprite.from(Shared.assets.getTexture('equipped-tick'));
        this.tickIcon.scale.set(1.25);
        this.tickIcon.anchor.set(0.5);
        this.tickIcon.position.set(-60, 30);
        this.rightPanel.view.addChild(this.tickIcon);

        this._selectContainer = new Sprite();
        this._selectContainer.anchor.set(0.5);
        this._selectContainer.y = 280;
        this.rightPanel.addChild(this._selectContainer);
        this.displayItemUnlockedStatus();
    }

    protected displayItemUnlockedStatus(): void
    {
        if (Settings.spineDebug) return;

        this._selectContainer.removeChildren();
        const data = this.app.data.getTrailData(Shared.game.garage.selectedTrail);
        const unlocked = Shared.game.shop.checkPurchased(Shared.game.garage.selectedTrail, 'trail');

        this.selectedText.visible = false;
        this.tickIcon.visible = false;

        if (unlocked)
        {
            this._currentPartID = Shared.game.garage.selectedTrail;
            this.selectedText.visible = true;
            this.tickIcon.visible = true;
            
            this.confirmButton.enable();
        }
        else
        {
            this.confirmButton.disable();
            
            const shopSystem = Shared.game.shop;
            const purchase = new PurchasePanel({
                enableCoinPurchase: true,
                enableDiamondPurchase: true,
                enableCancel: false,
                unlockCoinCost: data.coinPrice ?? 300,
                unlockDiamondCost: data.diamondPrice ?? 20,
            });

            this.selectedText.visible = false;
            this.tickIcon.visible = false;

            purchase.signals.coinBtnClick.connect((cost) =>
            {
                if (shopSystem.checkCoinsAvailable(cost))
                {
                    this.app.popup.presentConfirmPurchasePopup({
                        title: 'purchasePopup_title',
                        message: 'purchasePopup_message_generic',
                        confirm: 'purchasePopup_confirm',
                        cancel: 'purchasePopup_cancel',
                        plainSprite: TrailEntity.mountIcon(Shared.game.garage.selectedTrail, 200),
                        coinCost: cost,
                        onConfirm: () => this._purchaseItem(cost),
                    });
                }
            });

            purchase.signals.diamondBtnClick.connect((cost) =>
            {
                if (shopSystem.checkDiamondsAvailable(cost))
                {
                    this.app.popup.presentConfirmPurchasePopup({
                        title: 'purchasePopup_title',
                        message: 'purchasePopup_message_generic',
                        confirm: 'purchasePopup_confirm',
                        cancel: 'purchasePopup_cancel',
                        plainSprite: TrailEntity.mountIcon(Shared.game.garage.selectedTrail, 200),
                        diamondCost: cost,
                        onConfirm: () => this._purchaseItem(0, cost),
                    });
                }
            });

            this._selectContainer.addChild(purchase.view);
        }
    }

    protected updateTrailIcon(): void
    {
        const garage = Shared.game.garage;

        this._leftSwipeScroller.setCurrentIndex(garage.trails.indexOf(garage.selectedTrail));
    }

    protected buildLeftPanel(): void
    {
        this.leftPanel = new ShopPanel({
            title: '',
            w: this.panelWidth,
            h: this.panelHeight,
            radioButtonOptions: [
                {
                    iconOn: 'selector-all',
                    accessibleTitle: 'radio',
                    tabIndex: 1,
                    active: true,
                },
            ],
        });
    }
    
    private _purchaseItem(coins?: number, diamonds?: number): void
    {
        const shopSystem = Shared.game.shop;
        shopSystem.purchaseItem(Shared.game.garage.selectedTrail, 'trail', coins, diamonds);
        this._currentPartID = Shared.game.garage.selectedTrail;
        Shared.game.garage.saveVehicleSelection();
        this.displayItemUnlockedStatus();
        this._selectContainer.removeChildren();
        this._leftSwipeScroller.refreshContent();
        this.app.analytics.trackEvent(AnalyticsEvent.CONTENT_UNLOCKED, { game_item: `trail|${Shared.game.garage.selectedTrail}`});

        if(shopSystem.checkAllTypeUnlocked("trail")){
            this.app.analytics.trackEvent(AnalyticsEvent.PURCHASED_ALL_ITEMS, { game_item: `trail|${Shared.game.garage.selectedTrail}`, content_type: 'trail'});
        }
    }

    // ==== SWIPE SCROLLER ========================================================================

    private createSwipeScroller(): SwipeScroller
    {
        const scroller = new SwipeScroller({
            length: Shared.game.garage.trails.length,
            offsetY: -10,
            width: 274,
            height: 454,
            spacing: 150,
        }, this.leftSwipeScrollerContentProvider.bind(this));

        scroller.onSnap.connect(this.swipeScrollerSnapHandler.bind(this));
        scroller.onStartDrag.connect(this.swipeScrollerStartDragHandler.bind(this));

        return scroller;
    }

    /**
     * Provide sprites to a swipe scroller
     * @param index - Reference index for the content
     * @returns Sprite to be used inside the scroller
     */
    private leftSwipeScrollerContentProvider(index: number): Container
    {
        const garage = Shared.game.garage;
        const icon = TrailEntity.mountIcon(garage.trails[index], 200);

        icon.width = 270;
        icon.scale.y = icon.scale.x;

        const unlocked = Shared.game.shop.checkPurchased(garage.trails[index], 'trail');
        const selected = garage.trails[index] === this._currentPartID;

        if (!selected && !unlocked)
        {
            const lock = Sprite.from(Shared.assets.getTexture('icon-locked'));

            lock.scale.set(0.75 / icon.scale.x);
            lock.anchor.set(0.5);
            lock.position.set(-30 / icon.scale.x, 30 / icon.scale.x);

            icon.addChild(lock);
        }

        return icon;
    }

    /** Fires when scroller snaps to one of its items */
    private swipeScrollerSnapHandler(scroller: SwipeScroller): void
    {
        const index = scroller.getCurrentIndex();

        Shared.game.garage.setTrail(Shared.game.garage.trails[index]);
        this.displayItemUnlockedStatus();
    }

    /** Fires when user start dragging the scroller */
    private swipeScrollerStartDragHandler(): void
    {
        this.selectedText.visible = false;
        this.tickIcon.visible = false;
        this._selectContainer.removeChildren();
    }
    
    protected onCancelButtonPressed(): void {
        super.onCancelButtonPressed();
        
        Shared.game.shop.setTrail(this._startPartID);
        
    }
    
    protected onConfirmButtonPressed(): void {
        super.onConfirmButtonPressed();
        
        Shared.game.shop.setTrail(this._currentPartID);
        
        //this.app.analytics.trackEvent(AnalyticsEvent.CUSTOMISEPAGE_ITEMSELECTION_CLICK, { game_item: `garage|trails|${this._currentPartID}` });
        
        Shared.game.garage.saveVehicleSelection();
    }
}
